import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getCart, mergeCart, saveCart } from '@/api/cart';
import { useAuth } from '@/context/AuthContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { lineKey } from '@/utils/pricing';

const CartContext = createContext(null);

const STORAGE_KEY = 'tub.cart';

/**
 * Brings stored lines to the shape every mutator addresses them by: one line per
 * book, each carrying the `key` that identifies it.
 *
 * Two generations of saved cart need repairing here, and both used to leave a
 * line nothing could match:
 *
 *  - Carts from before language adaptations existed hold `{id, qty}` with no key
 *    at all. Deriving one only for display was not enough — remove/changeQty
 *    filter the *stored* lines, so such a line could never be deleted.
 *  - Carts from when the key included the chosen languages can hold several
 *    lines for the same book (`2`, `2::French`). Those are folded into one,
 *    keeping every adaptation chosen across them, so nobody is charged twice for
 *    a book they can only own once.
 *
 * The key is always recomputed rather than trusted, which is what makes the
 * second repair happen at all.
 */
function normalise(lines) {
  const byKey = new Map();

  for (const line of lines) {
    const key = lineKey(line.id);
    const languages = [...(line.languages ?? [])];
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...line, key, languages: [...new Set(languages)].sort() });
      continue;
    }

    existing.languages = [...new Set([...existing.languages, ...languages])].sort();
    // Max, not sum: folding a book that was split across lines must never
    // inflate what someone is charged.
    existing.qty = Math.max(existing.qty ?? 1, line.qty ?? 1);
  }

  return [...byKey.values()];
}

/** Cart lines are the minimal source of truth: { key, id, qty, languages }.
 *  Product details and prices are joined from the catalog at read time so they
 *  are never stale — a cart left for a week cannot quote a price the shop has
 *  since changed.
 *
 *  One line per book: `key` is the book's id, and `languages` holds the extra
 *  adaptations chosen for it — the base language is always included. Choosing a
 *  different set for a book already in the cart edits that line rather than
 *  adding another. */
export function CartProvider({ children }) {
  const { user, isSignedIn } = useAuth();

  // Two homes, one at a time. A visitor with no account has nowhere but this
  // browser to keep a cart; a signed-in reader's cart lives on their account, so
  // it survives a closed tab, cleared site data, and a move to another device.
  const [anonymousLines, setAnonymousLines] = useLocalStorage(STORAGE_KEY, []);
  const [accountLines, setAccountLines] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  /**
   * The account whose cart has actually been read back, or null.
   *
   * There is a real window — signing in at checkout, where the merge takes a
   * round trip — in which `accountLines` is [] because nothing has arrived yet,
   * not because the cart is empty. Anything that acts on an empty cart has to be
   * able to tell those apart: checkout used to bounce the shopper back to the
   * home page mid-purchase because it could not.
   *
   * Held as "who we loaded" rather than a boolean so it is correct from the
   * FIRST render after signing in. A flag flipped inside the loading effect is
   * one render too late, and that render is exactly the one where the new
   * account's cart is still empty.
   */
  const [loadedUsername, setLoadedUsername] = useState(null);

  const username = user?.username ?? null;
  const lines = isSignedIn ? accountLines : anonymousLines;

  // Who is signed in *now*, readable from a callback captured earlier.
  //
  // Checkout signs in and places the order from one callback, which closed over
  // a render where nobody was signed in. A cart mutator taken from that render
  // would route to the anonymous browser cart and a refresh would find no
  // account to read — so the current identity is read at call time, not at
  // render time.
  const authRef = useRef({ isSignedIn, username });
  authRef.current = { isSignedIn, username };

  /**
   * Orders local edits against server reads.
   *
   * Both change the same cart, and a slow response must never undo a change the
   * reader made while it was in flight — buy a book and a merge that started
   * before the purchase could put it straight back. Every edit and every read
   * takes a ticket; a read applies its result only if no ticket was issued after
   * its own.
   */
  const ticket = useRef(0);
  const takeTicket = useCallback(() => (ticket.current += 1), []);

  const setLines = useCallback(
    (updater) => {
      takeTicket();
      return authRef.current.isSignedIn ? setAccountLines(updater) : setAnonymousLines(updater);
    },
    [setAnonymousLines, takeTicket],
  );

  // Tracks the account whose cart is loaded, so the effect below can tell a
  // fresh sign-in from a re-render, and one account from another.
  const loadedFor = useRef(null);
  const anonymousRef = useRef(anonymousLines);
  anonymousRef.current = anonymousLines;

  /** Reads the account's cart, discarding the answer if it arrives too late. */
  const loadAccountCart = useCallback(
    (name, { merging = [], signal } = {}) => {
      const mine = takeTicket();

      // Anything collected before signing in is merged into the account rather
      // than replacing it or being thrown away; with nothing pending there is
      // nothing to merge, so the stored cart is simply read.
      const load = merging.length
        ? mergeCart(name, merging, { signal })
        : getCart(name, { signal });

      return load
        .then((cart) => {
          if (ticket.current !== mine) return false;
          setAccountLines(normalise(cart.lines ?? []));
          return true;
        })
        .finally(() => {
          // Only the newest read settles this: an older one finishing late would
          // announce a cart that is already being replaced. Set on failure too —
          // the fallback below still leaves a usable cart, and never settling
          // would strand checkout waiting for a read that is not coming.
          if (ticket.current === mine) setLoadedUsername(name);
        });
    },
    [takeTicket],
  );

  // The last change not yet written to the account, held so it can be flushed
  // rather than lost if the reader signs out inside the debounce window.
  const pendingSave = useRef(null);

  const flushSave = useCallback(() => {
    const job = pendingSave.current;
    pendingSave.current = null;
    if (job) saveCart(job.username, job.lines).catch(() => {});
  }, []);

  useEffect(() => {
    if (!username) {
      // Signed out: the account's cart stays on the server for next time, and
      // nothing of it is left on screen or in this browser. Anything added in
      // the last moment before signing out is written first — otherwise the
      // debounce timer is simply cancelled and the book quietly disappears.
      flushSave();
      loadedFor.current = null;
      takeTicket();
      setAccountLines([]);
      setLoadedUsername(null);
      return undefined;
    }

    if (loadedFor.current === username) return undefined;
    loadedFor.current = username;

    const controller = new AbortController();
    const pending = anonymousRef.current;

    loadAccountCart(username, { merging: pending, signal: controller.signal })
      .then((applied) => {
        // Merged, so the browser copy has served its purpose. Leaving it would
        // resurrect books deleted later on another device.
        if (applied && pending.length) setAnonymousLines([]);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // The account's cart is unreachable; fall back to whatever this browser
        // was holding rather than showing an empty cart that isn't empty.
        loadedFor.current = null;
        setAccountLines(normalise(pending));
      });

    return () => controller.abort();
  }, [username, setAnonymousLines, loadAccountCart, takeTicket, flushSave]);

  /**
   * Re-reads the cart from the account.
   *
   * Called after an order, where the server has already dropped the books that
   * were just bought — so this is what reconciles the cart with what was
   * actually paid for, rather than trusting the client to have guessed right.
   */
  const refresh = useCallback(() => {
    const name = authRef.current.username;
    if (!name) return Promise.resolve(false);
    return loadAccountCart(name).catch(() => false);
  }, [loadAccountCart]);

  /**
   * Adds a book, or updates the one already in the cart.
   *
   * A book occupies one line, so adding it again is how its adaptations get
   * changed: `languages` replaces whatever was chosen before rather than
   * creating a second line. The product page seeds its picker from this line, so
   * what comes back is a deliberate edit and not a stale empty selection.
   *
   * `single` marks a line that can only ever be one copy — a digital book, which
   * is an entitlement on an account rather than a thing you can own twice — so
   * re-adding it changes the adaptations without stacking a second charge.
   */
  const add = useCallback(
    (id, qty = 1, languages = [], { single = false } = {}) =>
      setLines((prev) => {
        const current = normalise(prev);
        const key = lineKey(id);
        const chosen = [...new Set(languages)].sort();
        const existing = current.find((line) => line.key === key);

        if (existing) {
          return current.map((line) =>
            line.key === key
              ? { ...line, languages: chosen, qty: single ? 1 : line.qty + qty }
              : line,
          );
        }
        return [...current, { key, id, qty: single ? 1 : qty, languages: chosen }];
      }),
    [setLines],
  );

  const changeQty = useCallback(
    (key, delta) =>
      setLines((prev) =>
        normalise(prev).map((line) =>
          line.key === key ? { ...line, qty: Math.max(1, line.qty + delta) } : line,
        ),
      ),
    [setLines],
  );

  const remove = useCallback(
    (key) => setLines((prev) => normalise(prev).filter((line) => line.key !== key)),
    [setLines],
  );

  const clear = useCallback(() => setLines(() => []), [setLines]);

  /**
   * Drops just the given lines. Checkout settles one currency at a time, so a
   * completed order must not empty the whole cart — the other currency's books
   * are still waiting to be bought.
   */
  const removeMany = useCallback(
    (keys) => {
      const drop = new Set(keys);
      setLines((prev) => normalise(prev).filter((line) => !drop.has(line.key)));
    },
    [setLines],
  );

  // Push every change back to the account. Debounced because a quantity stepper
  // fires several changes in a row, and each would otherwise be its own request.
  // Local state stays authoritative for the UI, so a failed save costs the
  // server that change and nothing on screen — the next save sends the whole
  // cart again, which repairs it. A sign-out flushes whatever is still pending.
  useEffect(() => {
    if (!username || loadedFor.current !== username) return undefined;

    pendingSave.current = { username, lines: accountLines };
    const timer = window.setTimeout(() => {
      flushSave();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [username, accountLines, flushSave]);

  const value = useMemo(() => {
    // Repaired on read as well as on write, so a cart saved by an older build
    // renders as one line per book straight away. The count is taken from the
    // repaired list too — counting the raw one made a book that older builds
    // had split across lines show up in the header badge twice.
    const current = normalise(lines);

    return {
      lines: current,
      count: current.reduce((sum, line) => sum + line.qty, 0),
      isEmpty: current.length === 0,
      /**
       * False while a signed-in account's cart is still in flight. Signed out
       * it is always true: the anonymous cart is already in this browser.
       */
      isLoaded: !isSignedIn || loadedUsername === username,
      add,
      changeQty,
      remove,
      removeMany,
      clear,
      refresh,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    };
  }, [lines, isSignedIn, username, loadedUsername, isOpen, add, changeQty, remove, removeMany, clear, refresh]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
